import axios from "axios";
import { attachMockInterceptor } from "./setupMockInterceptors";
import {
  clearRegulatorPortalAndRedirect,
  getRegulatorPortalToken,
} from "../utils/tokenUtils";

/**
 * Axios instance for the regulator portal.
 *
 * Sends the AccessToken, never the IdToken: the BE only synthesizes the
 * regulator `audience` block on access tokens, and a regulator awaiting
 * approval has no group claim on either token — so the IdToken carries nothing
 * that would identify them.
 *
 * `accessTokenOverride` covers the login handshake, where we need to ask `/me`
 * who just signed in *before* deciding to persist the session under the
 * regulator storage keys.
 */
const createRegulatorPortalInstance = (accessTokenOverride) => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_ADMIN_PANEL_BACKEND_BASE_URL,
  });

  instance.interceptors.request.use(
    (config) => {
      config.headers = config.headers || {};
      const accessToken =
        accessTokenOverride ?? getRegulatorPortalToken()?.AccessToken;
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  attachMockInterceptor(instance);

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Only a stored session can be kicked out — a failed login handshake is
      // the caller's to handle and must not navigate the window.
      if (error.response?.status === 401 && !accessTokenOverride) {
        clearRegulatorPortalAndRedirect();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

export default createRegulatorPortalInstance;
